import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import { BankAccountRestDataProvider } from '../../../data/BankAccountRestDataProvider';
import { BankAccount } from '../../../models/BankAccount';

const BankAccountsTab: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const provider = React.useMemo(() => new BankAccountRestDataProvider(), []);

    const columns: DetailedListColumn<BankAccount>[] = [
        {
            key: 'name',
            header: t('screens:bankAccountSettings.columns.name'),
            dataField: 'name',
            type: 'string',
            required: true,
        },
        {
            key: 'ibanAccountNumber',
            header: t('screens:bankAccountSettings.columns.iban'),
            dataField: 'ibanAccountNumber',
            type: 'string',
        },
        {
            key: 'ledgerCode',
            header: t('screens:bankAccountSettings.columns.ledgerCode'),
            dataField: 'ledgerCode',
            type: 'string',
            required: true,
        },
        {
            key: 'isPrivate',
            header: t('screens:bankAccountSettings.columns.isPrivate'),
            dataField: 'isPrivate',
            type: 'checkbox',
            width: 80,
            fixedWidth: true,
            align: 'center',
            render: (item) => item.isPrivate
                ? <span className="badge bg-secondary">{t('common:yes')}</span>
                : null,
        },
    ];

    useEffect(() => {
        setLoading(true);
        provider.getAll()
            .then(setBankAccounts)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [provider]);

    const handleChange = async (
        _updated: BankAccount[],
        action: 'add' | 'edit' | 'delete',
        changed: BankAccount
    ) => {
        try {
            if (action === 'add') {
                await provider.add({ ...changed, id: crypto.randomUUID() });
            } else if (action === 'edit') {
                await provider.update(changed.id, changed);
            } else if (action === 'delete') {
                await provider.delete(changed.id);
            }
            setBankAccounts(await provider.getAll());
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <div>
            <SubsectionPanel title={t('screens:bankAccountSettings.title')}>
                {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
                <DetailedListComponent
                    dataSource={{ data: bankAccounts, setData: setBankAccounts }}
                    columns={columns}
                    rowKey={r => r.id}
                    onChange={handleChange}
                    editRole="Admin,Manager"
                    addRole="Admin,Manager"
                    deleteRole="Admin"
                />
                {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
            </SubsectionPanel>
        </div>
    );
};

export default BankAccountsTab;

